import * as Yup from 'yup';

import { useLocales as getLocales } from 'src/locales';
import { MultipleGiftbackConfigValueType } from 'src/graphql/generated';

const REGEX_EMAIL = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}$/;

export const COUPON_PRODUCT_SCOPE = {
  ALL_PRODUCTS: 'ALL_PRODUCTS',
  PRODUCTS: 'PRODUCTS',
  CATEGORIES: 'CATEGORIES',
} as const;

export const COUPON_PRODUCT_SCOPE_OPTIONS = [
  {
    label: 'Todos os produtos',
    value: COUPON_PRODUCT_SCOPE.ALL_PRODUCTS,
  },
  {
    label: 'Produtos selecionados',
    value: COUPON_PRODUCT_SCOPE.PRODUCTS,
  },
  {
    label: 'Categorias selecionadas',
    value: COUPON_PRODUCT_SCOPE.CATEGORIES,
  },
];

export const COUPON_SCOPE_MODE = {
  INCLUDE: 'INCLUDE',
  EXCLUDE: 'EXCLUDE',
} as const;

export const COUPON_SCOPE_MODE_OPTIONS = [
  {
    label: 'Incluir selecionados',
    value: COUPON_SCOPE_MODE.INCLUDE,
  },
  {
    label: 'Excluir selecionados',
    value: COUPON_SCOPE_MODE.EXCLUDE,
  },
];

const couponScopeOptionSchema = Yup.object().shape({
  label: Yup.string().required('Obrigatório'),
  value: Yup.number().required('Obrigatório'),
  integrationId: Yup.string().nullable(),
});

export const ConfigFormSchema = Yup.object().shape({
  name: Yup.string().required('Nome da Loja é obrigatório'),
  email: Yup.string()
    .required('Email é obrigatório')
    .matches(REGEX_EMAIL, 'Email precisa de endereço válido de email'),
  phone: Yup.string().min(10, 'Mínimo de 10 dígitos').required('Telefone é obrigatório'),
  taxDocument: Yup.string().required('Documento é obrigatório'),
  site: Yup.string().nullable(),
  profileImage: Yup.string().nullable(),
  currencyType: Yup.object()
    .shape({
      value: Yup.string().required('Obrigatório'),
      label: Yup.string().required('Obrigatório'),
    })
    .nullable(),
});

export const CURRENCY_TYPES_VALUE = [
  {
    label: 'Real',
    value: 'REAL',
  },
  {
    label: 'Dolar',
    value: 'DOLAR',
  },
];

export type TConfigFormSchema = Yup.InferType<typeof ConfigFormSchema>;

export const configFormSchemaDefaultValues: TConfigFormSchema = {
  name: '',
  email: '',
  phone: '',
  taxDocument: '',
  site: '',
  profileImage: '',
  currencyType: CURRENCY_TYPES_VALUE[0],
};

export const ConfigFormGiftBackSchema = Yup.object().shape({
  dueDate: Yup.string().required('Insira quantidade de dias'),
  cumulative: Yup.bool().nullable(),
  couponProductScope: Yup.object()
    .shape({
      value: Yup.string().required('Obrigatório'),
      label: Yup.string().required('Obrigatório'),
    })
    .required('Obrigatório'),
  couponScopeMode: Yup.object().when('couponProductScope', {
    is: (scope: any) =>
      scope?.value === COUPON_PRODUCT_SCOPE.PRODUCTS ||
      scope?.value === COUPON_PRODUCT_SCOPE.CATEGORIES,
    then: (schema) =>
      schema
        .shape({
          value: Yup.string().required('Obrigatório'),
          label: Yup.string().required('Obrigatório'),
        })
        .required('Obrigatório'),
    otherwise: (schema) => schema.nullable(),
  }),
  couponScopeProducts: Yup.array().when('couponProductScope', {
    is: (scope: any) => scope?.value === COUPON_PRODUCT_SCOPE.PRODUCTS,
    then: (schema) => schema.of(couponScopeOptionSchema).min(1, 'Selecione ao menos um produto'),
    otherwise: (schema) => schema.of(couponScopeOptionSchema).nullable(),
  }),
  couponScopeCategories: Yup.array().when('couponProductScope', {
    is: (scope: any) => scope?.value === COUPON_PRODUCT_SCOPE.CATEGORIES,
    then: (schema) => schema.of(couponScopeOptionSchema).min(1, 'Selecione ao menos uma categoria'),
    otherwise: (schema) => schema.of(couponScopeOptionSchema).nullable(),
  }),
  multipleConfigs: Yup.array()
    .of(
      Yup.object().shape({
        id: Yup.string().nullable(),
        valueType: Yup.object()
          .shape({
            value: Yup.string().required('Obrigatório'),
            label: Yup.string().required('Obrigatório'),
          })
          .required('Preenchimento Obrigatório'),
        value: Yup.string().nullable(),
        minPurchaseValueForCreate: Yup.string().nullable(),
        minimumValueType: Yup.object()
          .shape({
            value: Yup.string().required('Obrigatório'),
            label: Yup.string().required('Obrigatório'),
          })
          .required('Preenchimento Obrigatório'),
        maximumDiscountPercentage: Yup.string().nullable(),
        discountLimitEnabled: Yup.bool().nullable(),
        discountLimit: Yup.string().nullable(),
        minPurchaseValueForRedeem: Yup.string().nullable(),
        tags: Yup.array()
          .of(
            Yup.object().shape({
              label: Yup.string().required(),
              value: Yup.string().required(),
            })
          )
          .nullable(),
      })
    )
    .min(1, 'Adicione ao menos uma configuração')
    .required('Obrigatório'),
});

export type TConfigFormGiftBackSchema = Yup.InferType<typeof ConfigFormGiftBackSchema>;

export const TYPES_VALUE = [
  {
    label: getLocales().currentLang.currencyShort,
    value: MultipleGiftbackConfigValueType.FixedValue,
  },
  {
    label: '%',
    value: MultipleGiftbackConfigValueType.Percentage,
  },
];
export const ConfigFormGiftBackSchemaDefaultValues: TConfigFormGiftBackSchema = {
  dueDate: '',
  cumulative: false,
  couponProductScope: COUPON_PRODUCT_SCOPE_OPTIONS[0],
  couponScopeMode: COUPON_SCOPE_MODE_OPTIONS[0],
  couponScopeProducts: [],
  couponScopeCategories: [],
  multipleConfigs: [
    {
      id: '',
      valueType: TYPES_VALUE[1],
      minimumValueType: TYPES_VALUE[1],
      value: '',
      minPurchaseValueForCreate: '',
      maximumDiscountPercentage: '',
      discountLimitEnabled: false,
      discountLimit: '',
      minPurchaseValueForRedeem: '',
      tags: [],
    },
  ],
};
